import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../utils/clickOutSide';
import ChevronDown from '../../assets/icons/select-chevron-down.svg?react';

interface ComboboxProps {
  otherClasses?: {
    container?: string;
    label?: string;
    button?: string;
    listContainer?: string;
    list?: string;
  };
  list: {
    label: string;
    value: string;
  }[];
  onSelect: (e: string) => void;
  value: string;
  label?: string;
  placeholder?: string;
  totalItems: number;
  hidden?: boolean;
}

const Combobox: React.FC<ComboboxProps> = ({
  otherClasses,
  list,
  onSelect,
  value,
  label,
  placeholder,
  totalItems,
  hidden,
}) => {
  const comboboxClasses = clsx(
    otherClasses?.container,
    ' flex-row items-center gap-2.5 w-fit relative',
    hidden ? 'hidden' : 'flex',
  );
  const [inputValue, setInputValue] = useState<string>(value);
  const [toggle, setToggle] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const ref = useRef(null);
  useClickOutside({ ref, setToggle });

  const labelClasses = clsx(
    otherClasses?.label,
    'text-neutral-tones-text/40 uppercase -tracking-form-input lg:-tracking-button text-base leading-5 font-medium lg:text-lg lg:leading-6 whitespace-nowrap',
  );
  const buttonClasses = clsx(
    otherClasses?.button,
    'w-20 pr-8 text-left global-transition ring border-transparent flex items-center gap-2 justify-between font-normal font-inter-variable  disabled:ring-neutral-tones-text/35 disabled:text-neutral-tones-text/35 disabled:cursor-not-allowed cursor-pointer',
    toggle
      ? `ring-brand-byzantine ring-2`
      : `${value === '' ? `ring-neutral-tones-text/50 ` : `ring-neutral-tones-text`}`,
    value === '' ? 'text-neutral-tones-text/35' : 'text-neutral-tones-text',
    'px-2 py-1 bg-transparent text-sm rounded-sm',
  );
  const listContainerClasses = clsx(
    otherClasses?.listContainer,
    'bg-light-translucent-background dark:bg-dark-translucent-background rounded-10 ring-neutral-tones-text/35 ring text-left py-15 px-5 absolute z-10  w-full max-h-64 overflow-auto global-scrollbar-style',
    'bottom-10 ',
  );
  const listClasses = clsx(
    otherClasses?.list,
    'border-b border-b-neutral-tones-text/10 first:!pt-0 py-2.5 last:pb-0 last:border-0 last:pt-2.5 cursor-pointer',
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setToggle(false);
      const numValue = Number(inputValue);
      if (numValue > totalItems) {
        onSelect(totalItems.toString());
        setInputValue(totalItems.toString());
      } else if (numValue < 1) {
        onSelect('1');
        setInputValue('1');
      } else {
        onSelect(inputValue);
      }
    }
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);
  return (
    <div ref={ref} className={comboboxClasses} data-testid="combobox">
      {label && (
        <label
          onClick={() => {
            setToggle(!toggle);
            inputRef.current?.focus();
          }}
          className={labelClasses}
        >
          {label}
        </label>
      )}
      <div className="relative">
        {!hidden && (
          <input
            type="number"
            name="combobox-input"
            className={clsx(
              buttonClasses,
              '[-webkit-appearance]:none',
              '[appearance:textfield]',
              '[&::-webkit-outer-spin-button]:appearance-none',
              '[&::-webkit-inner-spin-button]:appearance-none',
            )}
            placeholder={placeholder}
            min={1}
            max={totalItems}
            ref={inputRef}
            onChange={(e) => {
              const value = e.target.value;
              if (value === '' || /^\d+$/.test(value)) {
                setInputValue(value);
              }
            }}
            onKeyDown={handleKeyDown}
            value={inputValue}
            onClick={(e) => {
              e.preventDefault();
              setToggle(!toggle);
            }}
          />
        )}
        <button
          onClick={() => {
            setToggle(!toggle);
            inputRef.current?.focus();
          }}
          className="absolute top-2/4 right-2 -translate-y-2/4"
        >
          <ChevronDown
            className={clsx(
              'global-transition',
              '[&>path]:fill-neutral-tones-text',
              toggle ? 'rotate-180' : 'rotate-0',
            )}
          />
        </button>
      </div>

      {toggle && (
        <ul className={listContainerClasses}>
          {list?.map((node, index) => (
            <li key={index} className={listClasses}>
              <button
                className={clsx(
                  'font-inter-variable disabled:text-neutral-tones-text/35 w-full cursor-pointer text-left font-normal disabled:cursor-not-allowed',
                  node?.value === value
                    ? 'text-brand-byzantine'
                    : 'hover:text-neutral-tones-text text-neutral-tones-text/80',
                  '-tracking-form-input text-base',
                )}
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(node?.value);
                  setToggle(false);
                  setInputValue(node?.value);
                }}
                disabled={totalItems < Number(node?.value)}
              >
                {node?.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default Combobox;
